/**
 * 브랜드 허브 상세 「개요」의 **질문 한 벌** — 화면 조건 → 경계 요청.
 *
 * 🔑 로그인한 상세(`useBrandHubData`)와 공유 페이지(`server/hub/share-brand-source`)가 **같은 함수로** 요청을 짓는다 —
 *   한쪽만 고치면 공유받은 사람이 다른 숫자를 본다.
 * 🔑 집계는 «브랜드 태그»(`canonicalBrands`) + `beautyEval` + `basicOnly` 짝이다(브랜드 랭킹과 같은 모수).
 * ⚠️ 실물(광고·상품)은 태그가 아니라 정규 브랜드 축(`brand` · `canonicalBrand`)으로 묻는다.
 * ⚠️ `endMs` 가 0 이면 오늘까지다(`toBrandHubPeriod`).
 * ⚠️ 페르소나는 **소셜 글 원장에만** 걸린다(랭킹·피벗·글·계정) — 광고·상품 원장에는 그 축이 없다(`brand-hub-scope-note`).
 */

import type { AdListScope } from 'src/types/ads-meta/ads-meta-scope';
import type { MomentPersonaSelection } from 'src/types/moments/moment-scope';
import type { BrandPersonaToken } from 'src/utils/brand/brand-persona-persist';
import type { ProductRankingSearchScope } from 'src/types/product-rankings/product-rankings-scope';
import type { MomentFeedPageRequest, MomentBrandRankingRequest, MomentAccountThumbsRequest, MomentAnalyticsPivotRequest, MomentCategoryBrandRankingRequest } from 'src/types/moments/moment-ops';

import { toAnalyticsPeriod } from 'src/utils/pivot/period';
import { personaTokensToSelection } from 'src/utils/brand/brand-persona-persist';

import { toMediaOrganicScope } from 'src/sections/search-all/scope/search-all-media-scope';
import { type MediaChannel, type MediaCountry, MEDIA_ORGANIC_CHANNELS } from 'src/sections/search-all/constants/search-all-media';

import { BRAND_HUB_DEFAULT_DAYS } from '../utils/brand-hub-period';
import { toCreatorChannels } from '../constants/brand-hub-channels';
import { brandHubProductRegion } from '../utils/brand-hub-product-region';

/** 개요를 정하는 조건 — 상세 머리의 기간·채널·국가·제품군·페르소나 */
export type BrandHubConditions = {
  name: string;
  days: number;
  /** 기간의 끝(밀리초) — 0 이면 오늘 */
  endMs: number;
  channels: readonly MediaChannel[];
  countries: readonly MediaCountry[];
  /** 고른 제품군의 색인값 — 비면 안 가린다 */
  categoryTerms: readonly string[];
  /** 고른 페르소나 토큰 — 비었거나 셋 다면 안 가린다(`brandHubPersona`) */
  personas: readonly BrandPersonaToken[];
};

/** 한 번에 받는 실물 수 — 개요는 앞 `BRAND_HUB_OVERVIEW_CARDS` 장만 편다 */
export const BRAND_HUB_SAMPLE = 12;
/** 개요에서 맛보이는 실물 수 — 한 줄에 다 서는 만큼 */
export const BRAND_HUB_OVERVIEW_CARDS = 4;
/** 많이 다룬 계정 수 */
export const BRAND_HUB_TOP_ACCOUNTS = 10;
/** 「같은 기간 상위 브랜드」를 셀 때 훑는 수 — 목록과 같은 수(`BRAND_HUB_LIST_SIZE`) · 서버 상한이 1,000 이다 */
export const BRAND_HUB_RANK_SCAN = 1000;

/** 피벗 일곱 — 행 축 · 열 축 · 지표. ⚠️ 추이 둘은 도달로 묻는다(응답의 `count` 가 언급 수다) */
export const BRAND_HUB_PIVOTS = {
  daily: ['source', 'day', 'view'],
  trend: ['source', 'week', 'view'],
  coBrands: ['brand', '', 'count'],
  ingredients: ['ingredient', '', 'count'],
  ingredientsRaw: ['ingredientRaw', '', 'count'],
  concerns: ['concern', '', 'count'],
  concernsRaw: ['concernRaw', '', 'count'],
  hashtags: ['hashtag', '', 'count'],
  markets: ['market', '', 'view'],
} as const;

export type BrandHubPivotKey = keyof typeof BRAND_HUB_PIVOTS;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * 브랜드 허브를 **조건 없이** 연 상세의 조건 — `paths.service.brandHubFresh` 가 여는 그 화면이다.
 * 🔑 밖에서 브랜드 하나의 수를 미리 셀 때(한 번에 찾기 · 시작하기) 이것으로 물어야 들어가서 보는 수와 같다.
 * ⚠️ 기간은 허브의 기본(최근 30일)이고 채널·국가·제품군·페르소나는 비운다(`scope=all`).
 */
export function brandHubFreshConditions(name: string): BrandHubConditions {
  return { name, days: BRAND_HUB_DEFAULT_DAYS, endMs: 0, channels: [], countries: [], categoryTerms: [], personas: [] };
}

/** 집계·글 목록의 기간 */
export function brandHubPeriod(c: BrandHubConditions, now: number = Date.now()) {
  return toAnalyticsPeriod(c.days, c.endMs || now);
}

/**
 * 고른 페르소나 → 질의의 조건.
 * @returns 비었거나 셋 다면 `undefined` — 요청에 싣지 않는다(유선 모양이 그대로다)
 */
export function brandHubPersona(c: BrandHubConditions): MomentPersonaSelection | undefined {
  return personaTokensToSelection(c.personas);
}

/** 랭킹 조회에 거는 좁히기 — 피벗의 `filter` 와 같은 조건(피벗은 페르소나를 `personas` 칸으로 받는다) */
export function brandHubNarrow(c: BrandHubConditions) {
  const persona = brandHubPersona(c);
  return {
    ...(c.channels.length > 0 ? { platforms: [...c.channels] } : {}),
    ...(c.countries.length > 0 ? { countries: [...c.countries] } : {}),
    ...(c.categoryTerms.length > 0 ? { categoryTerms: [...c.categoryTerms] } : {}),
    ...(persona ? { persona } : {}),
  };
}

/** 머리 요약의 기간 줄 — 수와 공식 계정(`detail`)을 함께 받는다 */
export function brandHubHeadRequest(c: BrandHubConditions, now: number = Date.now()): MomentBrandRankingRequest {
  return { period: brandHubPeriod(c, now), orderBy: 'mentions', names: [c.name], size: 1, detail: true, ...brandHubNarrow(c) };
}

/** 최근 7일(`week: 'recent'`) · 그 앞 7일(`'prev'`) 언급 */
export function brandHubWeekRequest(c: BrandHubConditions, week: 'recent' | 'prev', now: number = Date.now()): MomentBrandRankingRequest {
  const end = c.endMs || now;
  const period = toAnalyticsPeriod(7, week === 'recent' ? end : end - 7 * DAY_MS);
  return { period, orderBy: 'mentions', names: [c.name], size: 1, ...brandHubNarrow(c) };
}

/** 피벗 한 장 */
export function brandHubPivotRequest(c: BrandHubConditions, key: BrandHubPivotKey, now: number = Date.now()): MomentAnalyticsPivotRequest {
  const [rowAxis, columnAxis, metric] = BRAND_HUB_PIVOTS[key];
  const { persona, ...narrow } = brandHubNarrow(c);
  return {
    keywords: [],
    period: brandHubPeriod(c, now),
    rowAxis,
    columnAxis,
    metric,
    filter: { brands: [c.name], beautyEval: true, basicOnly: true, ...narrow, ...(persona ? { personas: persona } : {}) },
  };
}

/** 집행 중 광고 — 🚨 브랜드는 `brand`(canonicalBrand 정확히)로 건다 · 국가·제품군도 머리 조건 그대로 */
export function brandHubAdsScope(c: BrandHubConditions): AdListScope {
  return {
    brand: c.name,
    active: ['active'],
    groupBy: 'image',
    sort: 'duration',
    from: 0,
    limit: BRAND_HUB_SAMPLE,
    ...(c.countries.length > 0 ? { country: [...c.countries] } : {}),
    ...(c.categoryTerms.length > 0 ? { categoryTerms: [...c.categoryTerms] } : {}),
  };
}

/** 광고 통계 — 머리의 「집행 중 광고」와 같은 모수 */
export function brandHubAdStatsScope(c: BrandHubConditions): AdListScope {
  return {
    brand: c.name,
    active: ['active'],
    groupBy: 'image',
    ...(c.countries.length > 0 ? { country: [...c.countries] } : {}),
    ...(c.categoryTerms.length > 0 ? { categoryTerms: [...c.categoryTerms] } : {}),
  };
}

/** 판매몰 상품 — 국가는 판매몰 지역으로 옮겨 건다(`brandHubProductRegion`) */
export function brandHubProductsScope(c: BrandHubConditions): ProductRankingSearchScope {
  const region = brandHubProductRegion([...c.countries]).region;
  return {
    q: '',
    canonicalBrand: c.name,
    limit: BRAND_HUB_SAMPLE,
    ...(region ? { region } : {}),
    ...(c.categoryTerms.length > 0 ? { categoryTerms: [...c.categoryTerms] } : {}),
  };
}

/** 반응 좋은 글 — 브랜드 태그로 찾는다(검색어가 아니다) · 허브는 오가닉 채널 전부를 편다 */
export function brandHubPostsRequest(c: BrandHubConditions, page: number, pageSize: number, sort: 'popular' | 'recent' = 'popular', formats: readonly ('video' | 'image')[] = [], now?: number): MomentFeedPageRequest {
  const scope = toMediaOrganicScope(
    { query: '', tab: 'organic', days: c.days, channels: [...c.channels], formats: [...formats], countries: [...c.countries], languages: [], sort },
    c.endMs || now,
    MEDIA_ORGANIC_CHANNELS,
    [c.name],
    [...c.categoryTerms],
  );
  const persona = brandHubPersona(c);
  return {
    ...scope,
    ...(persona ? { persona } : {}),
    page,
    pageSize,
    sortType: sort === 'recent' ? 'latest' : 'popular',
    sourceFieldSet: 'searchFeed',
    documentLimit: null,
  };
}

/**
 * 많이 다룬 계정 — 줄마다 실물 컷이 함께 온다. 채널은 SNS 다섯과 겹치는 것만(`toCreatorChannels` · 크리에이터 면과 같다).
 * 고른 채널이 SNS 와 하나도 안 겹치면 브랜드를 비워 보낸다 — 서버가 묻지 않고 빈 줄을 준다.
 */
export function brandHubAccountsRequest(c: BrandHubConditions, now: number = Date.now()): MomentAccountThumbsRequest {
  const creator = toCreatorChannels(c.channels);
  const persona = brandHubPersona(c);
  return {
    period: brandHubPeriod(c, now),
    brands: creator.none ? [] : [c.name],
    size: BRAND_HUB_TOP_ACCOUNTS,
    platforms: [...creator.channels],
    countries: [...c.countries],
    categoryTerms: [...c.categoryTerms],
    ...(persona ? { persona } : {}),
  };
}

/** 같은 기간 상위 브랜드 — 머리 조건을 그대로 따른다(⛔ 이 `total` 로 점유율을 내지 마라) */
export function brandHubPeersRequest(c: BrandHubConditions, now: number = Date.now()): MomentBrandRankingRequest {
  return { period: brandHubPeriod(c, now), orderBy: 'mentions', size: BRAND_HUB_RANK_SCAN, ...brandHubNarrow(c) };
}

/**
 * 상위 브랜드 훑기(`size` 개 순위)의 캐시 열쇠 — 목록(`universe`)과 상세(`peers`)가 **함께** 쓴다.
 * 🔑 열쇠는 요청 본문을 정하는 값 전부다 — 열쇠가 같으면 요청도 같아 한쪽이 받은 답을 다른 쪽이 그대로 쓴다
 *   (기본 조건에서 목록 → 상세로 들어가도 1,000개 집계를 다시 묻지 않는다).
 * ⚠️ 기간은 일 단위(`toAnalyticsPeriod`)라 `days`·`endMs` 로 갈음한다.
 * @param op 목록이 제품군을 걸면 다른 질문(`ranking.categoryBrands`)이라 갈라 둔다
 */
export function brandHubRankScanKey(
  op: 'brands' | 'categoryBrands',
  a: {
    days: number;
    endMs: number;
    orderBy: 'mentions' | 'reach';
    size: number;
    query: string;
    names: readonly string[];
    channels: readonly string[];
    countries: readonly string[];
    categoryTerms: readonly string[];
    personas: readonly string[];
  },
) {
  return ['brand-hub', 'rank-scan', op, a.days, a.endMs, a.orderBy, a.size, a.query, a.names, a.channels, a.countries, a.categoryTerms, a.personas] as const;
}

/** 「전체 브랜드」 순위를 정하는 값 — 목록 조건 그대로(`query`·`names` 는 목록 검색칸 · 상세 머리는 비운다) */
export type BrandHubRankScanInput = {
  days: number;
  endMs: number;
  orderBy: 'mentions' | 'reach';
  query: string;
  names: readonly string[];
  channels: readonly string[];
  countries: readonly string[];
  categoryTerms: readonly string[];
  personas: readonly string[];
};

/** 순위 훑기 질문 한 벌 — 열쇠와 본문이 한 값에서 나온다 */
export type BrandHubRankScan =
  | { key: ReturnType<typeof brandHubRankScanKey>; op: 'brands'; params: MomentBrandRankingRequest }
  | { key: ReturnType<typeof brandHubRankScanKey>; op: 'categoryBrands'; params: MomentCategoryBrandRankingRequest };

/**
 * 「전체 브랜드」 목록의 순위 훑기(`BRAND_HUB_RANK_SCAN` 개) — 목록과 상세 머리의 「N위」가 **같은 질문**을 쓴다.
 * 🚨 제품군이 걸리면 짝 맞춤 질문(`ranking.categoryBrands`)이다 — 「같은 기간 상위 브랜드」(`brandHubPeersRequest`)와 다른 질문이다.
 * @returns 열쇠(`brandHubRankScanKey`) · 고른 op · 요청 본문
 */
export function brandHubRankScan(a: BrandHubRankScanInput, now: number = Date.now()): BrandHubRankScan {
  const persona = personaTokensToSelection(a.personas);
  const narrow = {
    ...(a.query ? { query: a.query } : {}),
    ...(a.names.length > 0 ? { names: [...a.names] } : {}),
    ...(a.channels.length > 0 ? { platforms: [...a.channels] } : {}),
    ...(a.countries.length > 0 ? { countries: [...a.countries] } : {}),
    ...(persona ? { persona } : {}),
  };
  const base = { period: toAnalyticsPeriod(a.days, a.endMs || now), orderBy: a.orderBy, size: BRAND_HUB_RANK_SCAN };
  if (a.categoryTerms.length > 0) {
    return { key: brandHubRankScanKey('categoryBrands', { ...a, size: BRAND_HUB_RANK_SCAN }), op: 'categoryBrands', params: { ...base, categoryTerms: [...a.categoryTerms], ...narrow } };
  }
  return { key: brandHubRankScanKey('brands', { ...a, size: BRAND_HUB_RANK_SCAN }), op: 'brands', params: { ...base, ...narrow } };
}

/** 이웃 브랜드의 공식 계정 — 보이는 이름만 짚어 `detail` 로 다시 묻는다 */
export function brandHubPeerFacesRequest(c: BrandHubConditions, names: string[], now: number = Date.now()): MomentBrandRankingRequest {
  return { period: brandHubPeriod(c, now), orderBy: 'mentions', names, detail: true, size: names.length, ...brandHubNarrow(c) };
}
